import AdminTable from 'common/AdminTable';
import { getRequest, postRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const initialFormData = {
  title: '',
  status: 'active',
  display_order: 0
};

const Announcements = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest('announcements?limit=1000');
      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        status: formData.status,
        display_order: formData.display_order
      };
      if (editingId) {
        await patchRequest(`announcements/${editingId}`, payload);
        toast.success('Announcement updated successfully');
      } else {
        await postRequest('announcements', payload);
        toast.success('Announcement added successfully');
      }
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      title: row.title || '',
      status: row.status || 'active',
      display_order: row.display_order ?? 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggle = async (id) => {
    try {
      await patchRequest(`announcements/${id}/status`, {
        status: records.find((r) => r.id === id)?.status === 'active' ? 'inactive' : 'active'
      });
      toast.success('Status updated successfully');
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This announcement will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteRequest(`announcements/${id}`);
        toast.success('Announcement deleted successfully');
        if (editingId === id) resetForm();
        fetchRecords();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to delete announcement');
      }
    });
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Title', accessor: 'title' },
    { Header: 'Order', accessor: 'display_order' },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <button
          type="button"
          className={`btn btn-sm ${row.original.status === 'active' ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => handleToggle(row.original.id)}
        >
          {row.original.status === 'active' ? 'Active' : 'Inactive'}
        </button>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-sm btn-info mx-1 my-1" onClick={() => handleEdit(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger mx-1 my-1" onClick={() => handleDelete(row.original.id)}>
            Delete
          </button>
        </>
      )
    }
  ];

  return (
    <div className="container-fluid px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <h2 className="m-0">Announcements</h2>
          <small className="text-muted">Manage hospital announcements and notices</small>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5 mb-4">
          <div className="card shadow border-0 h-100">
            <div className="card-header bg-primary text-white py-3">
              <h5 className="mb-0">{editingId ? 'Update Announcement' : 'Add Announcement'}</h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="title" className="adminlables">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className="form-control"
                    placeholder="e.g. OPD Timing Update"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="display_order" className="adminlables">
                    Display Order
                  </label>
                  <input
                    type="number"
                    id="display_order"
                    name="display_order"
                    className="form-control"
                    min="0"
                    value={formData.display_order}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="status" className="adminlables">
                    Status
                  </label>
                  <select id="status" name="status" className="form-control" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  {editingId && (
                    <button type="button" className="btn btn-light px-4" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-5" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow border-0">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Announcement List</h5>
            </div>
            <div className="card-body p-0">
              {loading && records.length === 0 ? (
                <div className="text-center p-5">Loading...</div>
              ) : (
                <AdminTable tableHeaders={tableHeaders} tableData={records} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Announcements;
